/**
 * Les images d'un dépôt dans le mode `?demo` (ARTO-131) : un dépôt de
 * photographies (voir `demoDepots.ts`), un lot rapproché qui garde deux
 * ambigus, des orphelins, un TIFF non pris en charge et un doublon
 * `.jpg`/`.jpeg`. Un rapprochement se refait avec les décisions de la galerie,
 * comme chez l'API : rien n'est lu dans un vrai fichier, les cas tiennent dans
 * une table de classes. Tout en mémoire, sans réseau ; aucune galerie ni
 * personne réelle.
 */
import { ApiError } from './api.js';
import {
  conflit,
  crochetsDemo,
  demoCorrespondance,
  enfants,
  il_y_a,
  introuvable,
  lotVide,
  lotsDe,
  morceauSemis,
  travail,
  trouver,
  type LotCorr,
} from './demoCorrespondance.js';
import { demoDepots, LIBELLE_PHOTOS } from './demoDepots.js';
import type { EntreeManifeste } from './depots.js';
import type {
  CandidatImage,
  CreationLotImages,
  DecisionsImages,
  ImageAmbigue,
  ImageIgnoree,
  ImageNonPrise,
  ImageOrpheline,
  ImageRapprochee,
  Rapprochement,
} from './images.js';
import type { LotReprise } from './reprise.js';

interface OeuvreDemo {
  oeuvreId: string;
  inventaire: string;
  titre: string;
}

const OEUVRES: readonly OeuvreDemo[] = [
  { oeuvreId: 'oeuvre-demo-a1', inventaire: 'A-1', titre: 'Étude pour un paysage' },
  { oeuvreId: 'oeuvre-demo-a2', inventaire: 'A-2', titre: 'Nature morte aux coings' },
  { oeuvreId: 'oeuvre-demo-a3', inventaire: 'A-3', titre: 'Fenêtre sur cour' },
  { oeuvreId: 'oeuvre-demo-b1', inventaire: 'B-1', titre: 'Hiver blanc' },
  { oeuvreId: 'oeuvre-demo-b2', inventaire: 'B-2', titre: 'Portrait de Jeanne' },
  { oeuvreId: 'oeuvre-demo-c7', inventaire: 'C-7', titre: 'Les toits de Montmartre' },
  { oeuvreId: 'oeuvre-demo-d4', inventaire: 'D-4', titre: 'Le port de Concarneau' },
  { oeuvreId: 'oeuvre-demo-e9', inventaire: 'E-9', titre: 'Barques à marée basse' },
  { oeuvreId: 'oeuvre-demo-f2', inventaire: 'F-2', titre: 'Lumières de Brest' },
  { oeuvreId: 'oeuvre-demo-g5', inventaire: 'G-5', titre: 'Autoportrait à la lampe' },
];

const oeuvre = (inventaire: string): OeuvreDemo => {
  const trouvee = OEUVRES.find((candidate) => candidate.inventaire === inventaire);
  if (!trouvee) throw new Error(`Œuvre de démonstration inconnue : ${inventaire}`);
  return trouvee;
};

type Classe =
  | { genre: 'rapproche'; inventaire: string; ordre: number }
  | { genre: 'doublon' }
  | {
      genre: 'ambigu';
      motif: ImageAmbigue['motif'];
      candidats: { inventaire: string; ordre: number | null }[];
    }
  | { genre: 'orphelin'; cles: string[] };

/** Ce que le nom de chaque fichier de la démo dit, d'après les motifs par défaut. */
const CLASSES: Readonly<Record<string, Classe>> = {
  'Atelier/A-1.jpg': { genre: 'rapproche', inventaire: 'A-1', ordre: 1 },
  'Atelier/A-1_2.jpg': { genre: 'rapproche', inventaire: 'A-1', ordre: 2 },
  'Atelier/A-2.jpg': { genre: 'rapproche', inventaire: 'A-2', ordre: 1 },
  'Atelier/A-3.png': { genre: 'rapproche', inventaire: 'A-3', ordre: 1 },
  'Atelier/B-1.jpeg': { genre: 'rapproche', inventaire: 'B-1', ordre: 1 },
  'Atelier/B-1.jpg': { genre: 'doublon' },
  'Atelier/B-2.webp': { genre: 'rapproche', inventaire: 'B-2', ordre: 1 },
  'Atelier/C-7.jpg': { genre: 'rapproche', inventaire: 'C-7', ordre: 1 },
  'Atelier/C-7_2.jpg': { genre: 'rapproche', inventaire: 'C-7', ordre: 2 },
  'Atelier/D-4.jpg': {
    genre: 'ambigu',
    motif: 'plusieurs_oeuvres',
    candidats: [
      { inventaire: 'D-4', ordre: 1 },
      { inventaire: 'E-9', ordre: 1 },
    ],
  },
  'Atelier/A-3 (2)-3.jpg': {
    genre: 'ambigu',
    motif: 'plusieurs_ordres',
    candidats: [
      { inventaire: 'A-3', ordre: 2 },
      { inventaire: 'A-3', ordre: 3 },
    ],
  },
  'Atelier/Z-9.jpg': { genre: 'orphelin', cles: ['Z-9'] },
  'Atelier/Brouillons/IMG_0412.jpg': { genre: 'orphelin', cles: ['IMG_0412'] },
};

const TYPES_ACCEPTES = new Set([
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/avif',
  'image/heic',
  'image/heif',
]);

const OCTETS_PAR_MORCEAU = 2 * 1024 ** 3;
const FICHIERS_PAR_MORCEAU = 500;

interface EtatImages {
  decisions: DecisionsImages;
  revue: Rapprochement | null;
}

const etats = new Map<string, EtatImages>();
const etatDe = (lotId: string): EtatImages => {
  const connu = etats.get(lotId);
  if (connu) return connu;
  const nouveau: EtatImages = { decisions: {}, revue: null };
  etats.set(lotId, nouveau);
  return nouveau;
};

const sousDossier = (chemin: string, dossier: string | null | undefined): boolean =>
  !dossier || chemin === dossier || chemin.startsWith(`${dossier}/`);

function candidat(inventaire: string, ordre: number | null): CandidatImage {
  const o = oeuvre(inventaire);
  return { oeuvreId: o.oeuvreId, inventaire: o.inventaire, titre: o.titre, ordre };
}

/** Le rapprochement d'un lot : les noms de fichier d'abord, les décisions de la galerie par-dessus. */
function calculer(idGalerie: string, lot: LotCorr, decisions: DecisionsImages): Rapprochement {
  const depotId = lot.vue.depotId ?? '';
  const dossier = lot.vue.options.dossier ?? null;
  const fichiers = demoDepots
    .pageManifeste(idGalerie, depotId)
    .filter(
      (f: EntreeManifeste) =>
        f.etat === 'accepte' &&
        f.type?.mime.startsWith('image/') === true &&
        sousDossier(f.chemin, dossier),
    )
    .sort((a, b) => a.chemin.localeCompare(b.chemin, 'fr', { numeric: true }));

  const rapproches: ImageRapprochee[] = [];
  const ambigus: ImageAmbigue[] = [];
  const orphelins: ImageOrpheline[] = [];
  const ignores: ImageIgnoree[] = [];
  const nonPrisEnCharge: ImageNonPrise[] = [];
  const vus = new Set(fichiers.map((f) => f.id));

  for (const fichier of fichiers) {
    const mime = fichier.type?.mime ?? '';
    if (!TYPES_ACCEPTES.has(mime)) {
      nonPrisEnCharge.push({ fichierId: fichier.id, chemin: fichier.chemin, mime });
      continue;
    }
    const classe = CLASSES[fichier.chemin] ?? { genre: 'orphelin', cles: [] };
    const decision = decisions[fichier.id];
    if (decision?.action === 'ignorer') {
      ignores.push({ fichierId: fichier.id, chemin: fichier.chemin, motif: 'decision' });
      continue;
    }
    if (decision?.action === 'rattacher') {
      const cible = OEUVRES.find((o) => o.oeuvreId === decision.oeuvreId);
      if (cible) {
        rapproches.push({
          fichierId: fichier.id,
          chemin: fichier.chemin,
          oeuvreId: cible.oeuvreId,
          inventaire: cible.inventaire,
          titre: cible.titre,
          ordre: decision.ordre ?? (classe.genre === 'rapproche' ? classe.ordre : 1),
          octets: fichier.octets,
          decide: true,
        });
        continue;
      }
      ambigus.push({
        fichierId: fichier.id,
        chemin: fichier.chemin,
        motif: 'decision_invalide',
        candidats: [],
      });
      continue;
    }
    switch (classe.genre) {
      case 'rapproche': {
        const cible = oeuvre(classe.inventaire);
        rapproches.push({
          fichierId: fichier.id,
          chemin: fichier.chemin,
          oeuvreId: cible.oeuvreId,
          inventaire: cible.inventaire,
          titre: cible.titre,
          ordre: classe.ordre,
          octets: fichier.octets,
          decide: false,
        });
        break;
      }
      case 'doublon':
        ignores.push({ fichierId: fichier.id, chemin: fichier.chemin, motif: 'doublon' });
        break;
      case 'ambigu':
        ambigus.push({
          fichierId: fichier.id,
          chemin: fichier.chemin,
          motif: classe.motif,
          candidats: classe.candidats.map((c) => candidat(c.inventaire, c.ordre)),
        });
        break;
      case 'orphelin':
        orphelins.push({
          fichierId: fichier.id,
          chemin: fichier.chemin,
          motif: 'aucune_oeuvre',
          cles: classe.cles,
        });
        break;
    }
  }

  const couvertes = new Set(rapproches.map((r) => r.oeuvreId));
  const oeuvresSansImage = OEUVRES.filter((o) => !couvertes.has(o.oeuvreId)).map((o) => ({
    oeuvreId: o.oeuvreId,
    inventaire: o.inventaire,
    titre: o.titre,
  }));

  // Aucun morceau tant qu'un ambigu reste ; sinon 500 fichiers ou 2 Gio au plus.
  const morceaux: Rapprochement['morceaux'] = [];
  if (ambigus.length === 0) {
    let courant = { rang: 1, fichiers: 0, octets: 0 };
    for (const image of rapproches) {
      if (
        courant.fichiers >= FICHIERS_PAR_MORCEAU ||
        (courant.fichiers > 0 && courant.octets + image.octets > OCTETS_PAR_MORCEAU)
      ) {
        morceaux.push(courant);
        courant = { rang: courant.rang + 1, fichiers: 0, octets: 0 };
      }
      courant.fichiers += 1;
      courant.octets += image.octets;
    }
    if (courant.fichiers > 0) morceaux.push(courant);
  }

  return {
    version: 1,
    lotId: lot.vue.id,
    etabliLe: new Date().toISOString(),
    compteurs: {
      images: rapproches.length + orphelins.length + ambigus.length + ignores.length,
      rapproches: rapproches.length,
      orphelins: orphelins.length,
      ambigus: ambigus.length,
      ignores: ignores.length,
      nonPrisEnCharge: nonPrisEnCharge.length,
      oeuvresSansImage: oeuvresSansImage.length,
      morceaux: morceaux.length,
      decisionsSansFichier: Object.keys(decisions).filter((id) => !vus.has(id)).length,
    },
    ambigus,
    orphelins,
    rapproches,
    ignores,
    nonPrisEnCharge,
    oeuvresSansImage,
    morceaux,
  };
}

/** Pose la revue d'un rapprochement terminé et fabrique les morceaux de son plan. */
function conclure(
  idGalerie: string,
  lot: LotCorr,
  destination: LotCorr[] = lotsDe(idGalerie),
): void {
  const etat = etatDe(lot.vue.id);
  const revue = calculer(idGalerie, lot, etat.decisions);
  etat.revue = revue;
  const { decisionsSansFichier: _ignore, ...compteurs } = revue.compteurs;
  lot.vue.rapprochement = { fait: true, compteurs };
  lot.vue.lignes = revue.compteurs.images;
  for (const morceau of revue.morceaux)
    destination.push(morceauSemis(lot.vue, 'images', morceau.rang, morceau.fichiers, 'depose'));
}

crochetsDemo.rapproche = (idGalerie, lot) => {
  if (lot.vue.parcours === 'images') conclure(idGalerie, lot);
};

function exigerLotImages(idGalerie: string, lotId: string): LotCorr {
  const lot = trouver(idGalerie, lotId);
  if (lot.vue.parcours !== 'images')
    throw conflit('lot_sans_images', 'Ce lot n’est pas un lot d’images tiré d’un dépôt.');
  return lot;
}

function exigerMorceauxLibres(idGalerie: string, lot: LotCorr): void {
  if (
    enfants(idGalerie, lot).some((morceau) =>
      ['enfile', 'en_cours', 'termine', 'echoue', 'scelle'].includes(morceau.vue.etat),
    )
  )
    throw conflit(
      'morceaux_lances',
      'Un morceau de ce lot est déjà lancé : déposez un nouveau lot pour reprendre autrement.',
    );
}

function defaire(idGalerie: string, lot: LotCorr): void {
  for (const morceau of enfants(idGalerie, lot)) morceau.vue.etat = 'annule';
}

/** Le lot de départ de la démo : rapproché, avec ses deux ambigus à trancher. */
function semer(idGalerie: string, rang: number): LotCorr[] {
  if (rang !== 0) return [];
  const depot = demoDepots
    .depots(idGalerie)
    .find((candidate) => candidate.libelle === LIBELLE_PHOTOS);
  if (!depot) return [];
  const lot: LotCorr = {
    vue: lotVide(idGalerie, 'images', {
      parcours: 'images',
      depotId: depot.id,
      etat: 'rapproche',
      sources: [{ champ: 'manifeste', nom: 'manifeste.json', octets: 4_096 }],
      options: {
        confidentiels: false,
        motifInventaire: null,
        dossier: null,
        remplacerPrincipale: false,
      },
      rapprochement: { fait: false, compteurs: null },
      creeLe: il_y_a(25 * 60_000),
    }),
    surcharges: null,
    transformation: null,
    refus: false,
    phase: null,
  };
  // La liste de la galerie n'existe pas encore : les morceaux rejoignent ce que l'on renvoie.
  const morceaux: LotCorr[] = [];
  conclure(idGalerie, lot, morceaux);
  return [lot, ...morceaux];
}
crochetsDemo.semer = semer;

export const demoImages = {
  creerLotImages(idGalerie: string, corps: CreationLotImages): LotReprise {
    const depot = demoDepots.depots(idGalerie).find((candidate) => candidate.id === corps.depotId);
    if (!depot) throw introuvable('Dépôt');
    if (!depot.utilisable)
      throw conflit(
        'depot_inutilisable',
        'Ce dépôt n’est pas utilisable : son inventaire n’est pas fait, ou un fichier attend l’antivirus.',
      );
    const dossier = corps.dossier.trim().replace(/^\/+|\/+$/g, '');
    if (dossier.split('/').includes('..'))
      throw new ApiError(400, 'validation.invalide', 'Chemin refusé.');
    const lot: LotCorr = {
      vue: lotVide(idGalerie, 'images', {
        parcours: 'images',
        depotId: depot.id,
        sources: [{ champ: 'manifeste', nom: 'manifeste.json', octets: 4_096 }],
        options: {
          confidentiels: false,
          motifInventaire: null,
          dossier: dossier === '' ? null : dossier,
          remplacerPrincipale: corps.remplacerPrincipale,
        },
        rapprochement: null,
      }),
      surcharges: null,
      transformation: null,
      refus: false,
      phase: null,
    };
    lotsDe(idGalerie).unshift(lot);
    return demoCorrespondance.lot(idGalerie, lot.vue.id);
  },

  rapprocher(idGalerie: string, lotId: string): LotReprise {
    const lot = exigerLotImages(idGalerie, lotId);
    if (lot.phase) throw conflit('travail_en_cours', 'Un travail de ce lot est déjà en cours.');
    exigerMorceauxLibres(idGalerie, lot);
    defaire(idGalerie, lot);
    etatDe(lotId).revue = null;
    lot.vue.etat = 'depose';
    lot.vue.rapprochement = { fait: false, compteurs: null };
    lot.vue.travail = travail('reprise:rapprocher', 'en_attente');
    lot.vue.revision += 1;
    lot.phase = { genre: 'rapprochement', depuis: Date.now() };
    return demoCorrespondance.lot(idGalerie, lotId);
  },

  rapprochement(idGalerie: string, lotId: string): Rapprochement {
    exigerLotImages(idGalerie, lotId);
    const revue = etatDe(lotId).revue;
    if (!revue) throw introuvable('Ce lot n’est pas encore rapproché. Revue');
    return structuredClone(revue);
  },

  decisions(idGalerie: string, lotId: string): DecisionsImages {
    exigerLotImages(idGalerie, lotId);
    return structuredClone(etatDe(lotId).decisions);
  },

  enregistrerDecisions(
    idGalerie: string,
    lotId: string,
    corps: { revision: number; decisions: DecisionsImages },
  ): LotReprise {
    const lot = exigerLotImages(idGalerie, lotId);
    if (lot.phase) throw conflit('travail_en_cours', 'Un travail de ce lot est déjà en cours.');
    if (corps.revision !== lot.vue.revision)
      throw conflit('lot_modifie', 'Le lot vient de changer : rechargez.');
    exigerMorceauxLibres(idGalerie, lot);
    defaire(idGalerie, lot);
    const etat = etatDe(lotId);
    etat.decisions = structuredClone(corps.decisions);
    etat.revue = null;
    lot.vue.etat = 'depose';
    lot.vue.rapprochement = null;
    lot.vue.revision += 1;
    return demoCorrespondance.lot(idGalerie, lotId);
  },
};
